/**
 * Featured Product Style 4
 *
 * Scroll-driven, pinned product showcase.
 *
 * Port note: the original XO/Futurer section relied on the <xo-scroll-scene>
 * custom element from xo-webcomponents.js. That framework is not present in
 * this theme, so the scene engine is reimplemented here using the same
 * algorithm, with data-* attributes replacing the xo-* ones:
 *
 *   xo-ready     -> data-ready
 *   xo-active    -> data-active
 *   xo-prev      -> data-prev
 *   xo-next      -> data-next
 *   xo-direction -> data-direction
 *
 * Multiple instances of the section on one page are supported.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-fp4]';
  var MOBILE_BREAKPOINT = 991;
  var BASE_THRESHOLD = 0.5;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function setFlag(el, name, on) {
    if (!el) return;
    if (on) {
      el.setAttribute(name, '');
    } else {
      el.removeAttribute(name);
    }
  }

  function debounce(fn, wait) {
    var timer = -1;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function FeaturedProduct4(root) {
    this.root = root;
    this.scene = root.querySelector('.featured-product-4__scene');
    this.sceneInner = root.querySelector('.featured-product-4__scene-inner');
    this.items = Array.prototype.slice.call(
      root.querySelectorAll('.featured-product-4__scene-item')
    );

    this.desktopWrapper = root.querySelector(
      '.featured-product-4__head--desktop .featured-product-4__wrapper'
    );
    this.desktopItems = Array.prototype.slice.call(
      root.querySelectorAll('.featured-product-4__head--desktop .featured-product-4__item')
    );
    this.mobileItems = Array.prototype.slice.call(
      root.querySelectorAll('.featured-product-4__head--mobile .featured-product-4__item')
    );
    this.dots = Array.prototype.slice.call(root.querySelectorAll('.featured-product-4__dot'));
    this.tabs = Array.prototype.slice.call(
      root.querySelectorAll('.featured-product-4__heading-button')
    );

    this.activeIndex = 0;
    // Last index actually painted. null until the first render, so the
    // opening slide is not treated as a transition from somewhere.
    this.renderedIndex = null;
    this.prevScrollY = window.scrollY;
    this.prevWidth = 0;
    this.ready = false;
    this.ticking = false;
    this.resizeObserver = null;

    this.onScroll = this.onScroll.bind(this);
    this.onHeadingClick = this.onHeadingClick.bind(this);
    this.onResize = debounce(this.handleResize.bind(this), 400);

    this.mount();
  }

  /**
   * The original applied a +0.3 threshold bump on macOS, where trackpad
   * scrolling covers the scene far faster than a mouse wheel.
   */
  FeaturedProduct4.prototype.getThreshold = function () {
    var isMac = /Mac/.test(navigator.userAgent);
    return isMac ? BASE_THRESHOLD + 0.3 : BASE_THRESHOLD;
  };

  FeaturedProduct4.prototype.isMobile = function () {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  };

  FeaturedProduct4.prototype.mount = function () {
    if (!this.scene || this.items.length === 0) return;

    // A single slide has nothing to scroll through.
    if (this.items.length <= 1) {
      this.activeIndex = 0;
      this.updateUI();
      this.bindHeadingClicks();
      return;
    }

    this.bindHeadingClicks();
    this.bindSwipe();

    if (this.isMobile()) {
      this.disableScene();
    } else {
      this.enableScene();
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.scene);
    } else {
      window.addEventListener('resize', this.onResize);
    }
  };

  FeaturedProduct4.prototype.enableScene = function () {
    var threshold = this.getThreshold();

    window.removeEventListener('scroll', this.onScroll);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    // One sticky viewport, plus one step of travel per transition. With n
    // slides there are n-1 transitions.
    //
    // The original used 100 * (n + 1/threshold) * threshold, which gives n
    // steps of travel for n-1 transitions, leaving a full slide's worth of
    // dead scroll pinned on the last product.
    var steps = Math.max(this.items.length - 1, 1);
    this.scene.style.height = 100 + steps * threshold * 100 + 'vh';

    this.ready = true;
    setFlag(this.scene, 'data-ready', true);
    this.updateUI();
    this.syncHeadings();
  };

  FeaturedProduct4.prototype.disableScene = function () {
    window.removeEventListener('scroll', this.onScroll);
    this.ready = false;
    setFlag(this.scene, 'data-ready', false);
    this.scene.style.height = '';

    // On mobile the CSS shows only the active item, so keep that flag and
    // clear every transition marker. Leaving data-skipped or data-park set
    // would carry a desktop resting transform into the mobile layout.
    this.items.forEach(
      function (item, i) {
        setFlag(item, 'data-active', i === this.activeIndex);
        setFlag(item, 'data-prev', false);
        setFlag(item, 'data-next', false);
        setFlag(item, 'data-skipped', false);
        item.removeAttribute('data-park');
      }.bind(this)
    );

    // Next enableScene() should treat the first paint as fresh.
    this.renderedIndex = null;

    this.syncHeadings();
  };

  FeaturedProduct4.prototype.onScroll = function () {
    if (this.ticking) return;
    this.ticking = true;

    window.requestAnimationFrame(
      function () {
        this.ticking = false;

        var rect = this.scene.getBoundingClientRect();
        var steps = Math.max(this.items.length - 1, 1);

        // Derive the step from the scene's measured height rather than
        // assuming innerHeight * threshold. The two drift apart when the
        // height rounds, or when mobile browser chrome resizes the viewport
        // mid-scroll, which strands the last slide.
        var travel = Math.max(rect.height - this.sceneInner.offsetHeight, 1);
        var step = travel / steps;

        // round, not trunc: each slide owns the scroll range centred on it,
        // so the swap happens at the midpoint instead of arriving late.
        var index = Math.round((rect.top * -1) / step);
        var activeIndex = clamp(index, 0, this.items.length - 1);

        this.scene.setAttribute(
          'data-direction',
          window.scrollY > this.prevScrollY ? 'down' : 'up'
        );
        this.prevScrollY = window.scrollY;

        this.setActive(activeIndex);
      }.bind(this)
    );
  };

  FeaturedProduct4.prototype.setActive = function (index) {
    var next = clamp(index, 0, this.items.length - 1);
    if (next === this.activeIndex) return;

    this.activeIndex = next;
    this.updateUI();
    this.syncHeadings();

    this.scene.dispatchEvent(
      new CustomEvent('fp4:change', {
        bubbles: true,
        detail: { activeIndex: this.activeIndex }
      })
    );
  };

  FeaturedProduct4.prototype.updateUI = function () {
    var active = this.activeIndex;
    var previous = this.renderedIndex;

    // Direction matters: a slide the user scrolled PAST should exit the way
    // the old active slide exits, not the way an upcoming one enters.
    // Without this, skipping several slides at speed leaves the in-between
    // ones holding the wrong transform.
    var goingForward = previous == null || active >= previous;

    this.items.forEach(function (item, i) {
      var isActive = i === active;
      var isPrev = i === active - 1;
      var isNext = i === active + 1;

      setFlag(item, 'data-active', isActive);
      setFlag(item, 'data-prev', isPrev);
      setFlag(item, 'data-next', isNext);

      // Any slide that is neither active nor an immediate neighbour was
      // jumped over. Park it on the side it belongs to so it is already in
      // the right place if the user scrolls back.
      if (!isActive && !isPrev && !isNext) {
        setFlag(item, 'data-skipped', true);
        item.setAttribute('data-park', i < active ? 'behind' : 'ahead');
      } else {
        setFlag(item, 'data-skipped', false);
        item.removeAttribute('data-park');
      }
    });

    this.scene.setAttribute('data-going', goingForward ? 'forward' : 'back');
    this.renderedIndex = active;
  };

  /**
   * Highlights the active heading and slides the desktop strip so that the
   * active heading sits flush with the strip's left edge.
   */
  FeaturedProduct4.prototype.syncHeadings = function () {
    var active = this.activeIndex;

    this.mobileItems.forEach(function (item, i) {
      item.classList.toggle('featured-product-4__item--modifier', i === active);
    });

    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('featured-product-4__dot--active', i === active);
    });

    this.tabs.forEach(function (tab, i) {
      tab.setAttribute('aria-selected', i === active ? 'true' : 'false');
    });

    // Only the first run of headings is highlighted; the trailing duplicates
    // exist purely so the strip reads as a continuous loop while it slides.
    this.desktopItems.forEach(function (item, i) {
      item.classList.toggle('featured-product-4__item--modifier', i === active);
    });

    if (!this.desktopWrapper) return;

    var target = this.desktopItems[active];
    if (!target) return;

    // offsetLeft is measured against the wrapper's own untransformed box, so
    // it is unaffected by the translate already applied. Using
    // getBoundingClientRect() here would compound the existing offset.
    this.desktopWrapper.style.transform = 'translateX(-' + target.offsetLeft + 'px)';
  };

  FeaturedProduct4.prototype.bindHeadingClicks = function () {
    this.root.addEventListener('click', this.onHeadingClick);
  };


  FeaturedProduct4.prototype.onHeadingClick = function (event) {
    // The mobile heading wraps a <button>, so match either element.
    var heading = event.target.closest(
      '.featured-product-4__heading-button, .featured-product-4__heading'
    );
    if (!heading || !this.root.contains(heading)) return;

    var index = Number(heading.getAttribute('data-index'));
    if (Number.isNaN(index)) return;

    if (this.ready) {
      // Scroll to the point in the pinned scene that maps to this slide.
      var threshold = this.getThreshold();
      var sceneTop = this.scene.getBoundingClientRect().top + window.scrollY;
      var target = sceneTop + index * window.innerHeight * threshold;

      window.scrollTo({
        top: target,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    } else {
      this.goToMobile(index);
    }
  };

  /**
   * Mobile slide change.
   *
   * The desktop scene cross-fades via [data-prev]/[data-next], which depend
   * on scroll position and so do not apply here. Instead the outgoing slide
   * is marked with a direction, the incoming one animates in from the
   * opposite side, and the heading strip scrolls to follow.
   */
  FeaturedProduct4.prototype.goToMobile = function (index) {
    var next = clamp(index, 0, this.items.length - 1);
    if (next === this.activeIndex) return;

    var forward = next > this.activeIndex;
    var current = this.items[this.activeIndex];
    var incoming = this.items[next];

    this.activeIndex = next;

    if (prefersReducedMotion()) {
      this.items.forEach(function (item, i) {
        setFlag(item, 'data-active', i === next);
        item.removeAttribute('data-enter');
      });
      this.syncHeadings();
      this.scrollHeadingIntoView();
      return;
    }

    // Re-trigger the enter animation even when tapping quickly.
    if (current) current.removeAttribute('data-enter');
    incoming.removeAttribute('data-enter');

    this.items.forEach(function (item, i) {
      setFlag(item, 'data-active', i === next);
    });

    incoming.setAttribute('data-enter', forward ? 'forward' : 'back');

    // Clear the flag once the animation is done so it can run again.
    var clear = function () {
      incoming.removeAttribute('data-enter');
      incoming.removeEventListener('animationend', clear);
    };
    incoming.addEventListener('animationend', clear);

    this.syncHeadings();
    this.scrollHeadingIntoView();

    this.scene.dispatchEvent(
      new CustomEvent('fp4:change', {
        bubbles: true,
        detail: { activeIndex: next }
      })
    );
  };

  /**
   * Keeps the active heading visible in the horizontally scrollable strip.
   */
  FeaturedProduct4.prototype.scrollHeadingIntoView = function () {
    var strip = this.root.querySelector(
      '.featured-product-4__head--mobile .featured-product-4__wrapper'
    );
    if (!strip) return;

    var item = this.mobileItems[this.activeIndex];
    if (!item) return;

    var target = item.offsetLeft - (strip.clientWidth - item.offsetWidth) / 2;

    strip.scrollTo({
      left: Math.max(0, target),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  /**
   * Horizontal swipe to move between slides on touch devices.
   *
   * Only acts once the gesture is clearly horizontal, so vertical page
   * scrolling is never hijacked.
   */
  FeaturedProduct4.prototype.bindSwipe = function () {
    var startX = 0;
    var startY = 0;
    var tracking = false;
    var self = this;

    var content = this.root.querySelector('.featured-product-4__content');
    if (!content) return;

    content.addEventListener(
      'touchstart',
      function (event) {
        if (self.ready || event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        tracking = true;
      },
      { passive: true }
    );

    content.addEventListener(
      'touchend',
      function (event) {
        if (!tracking) return;
        tracking = false;

        var touch = event.changedTouches[0];
        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;

        // Require a deliberate, mostly-horizontal swipe.
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

        self.goToMobile(self.activeIndex + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );
  };

  FeaturedProduct4.prototype.handleResize = function (entries) {
    var width = entries && entries[0] ? entries[0].contentRect.width : window.innerWidth;
    if (width === this.prevWidth) return;
    this.prevWidth = width;

    if (this.isMobile()) {
      if (this.ready) this.disableScene();
    } else {
      this.enableScene();
    }
  };

  FeaturedProduct4.prototype.destroy = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.root.removeEventListener('click', this.onHeadingClick);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  };

  var instances = new WeakMap();

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SECTION_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (instances.has(root)) return;
      instances.set(root, new FeaturedProduct4(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector(SECTION_SELECTOR);
    if (!root) return;
    var existing = instances.get(root);
    if (existing) existing.destroy();
    instances.delete(root);
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector(SECTION_SELECTOR);
    if (!root) return;
    var existing = instances.get(root);
    if (existing) existing.destroy();
    instances.delete(root);
  });
})();
